import { useEffect, useState, useRef, ReactNode, lazy, Suspense } from 'react';
import { Header } from './Header/Header';
import { S3Client } from '@aws-sdk/client-s3';
import { DynamoDBClient, ScanCommand, ScanCommandOutput, AttributeValue, GetItemCommand } from '@aws-sdk/client-dynamodb';
import { User } from '../models/User';
import { unmarshall } from '@aws-sdk/util-dynamodb';
import { LambdaClient } from '@aws-sdk/client-lambda';
import { LandingSection } from './LandingSection/LandingSection';
import { FeedSection } from './FeedSection/FeedSection';

// Eager Preload Lazy Components
const DocumentsSectionPromise = import('./DocumentsSection/DocumentsSection');
const DocumentsSection = lazy(() => DocumentsSectionPromise.then(m => ({ default: m.DocumentsSection })));

const PhotosSectionPromise = import('./PhotosSection/PhotosSection');
const PhotosSection = lazy(() => PhotosSectionPromise.then(m => ({ default: m.PhotosSection })));

const ContactsSectionPromise = import('./ContactsSection/ContactsSection');
const ContactsSection = lazy(() => ContactsSectionPromise.then(m => ({ default: m.ContactsSection })));

const VideosSectionPromise = import('./VideosSection/VideosSection');
const VideosSection = lazy(() => VideosSectionPromise.then(m => ({ default: m.VideosSection })));
import { motion, AnimatePresence, Variants } from 'framer-motion';
import { XIcon } from '@heroicons/react/solid';
import { Amplify } from 'aws-amplify';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Authenticator, useAuthenticator } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import logo from '../FamilyCouncilLogo.jpg';
import { MockDynamoDBClient, MockS3Client, MockLambdaClient, MOCK_USER, MOCK_USERS } from '../utils/ServiceMocks';
import { initializeRum, setTelemetryUser, recordTelemetry, isRumInitialized } from '../utils/TelemetryService';

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: 'us-east-1_8izkf6tti',
      userPoolClientId: '5atl4es9f61nkhjnckb4fua1qi',
      identityPoolId: 'us-east-1:9e954036-c53c-4016-b0c2-81d42cbb1a14',
    }
  }
});

const isDemo = () => {
  if (typeof window === 'undefined') return false;
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('demo') === 'true' || (window as any).isDemoMode) {
    sessionStorage.setItem('isDemoMode', 'true');
    return true;
  }
  return sessionStorage.getItem('isDemoMode') === 'true';
};

// PREMIUM Section Wrapper: Optimized for Safari (WebKit) performance
const SectionWrapper = ({ children, id, noStyle }: { children: ReactNode; id: string; noStyle?: boolean }) => {
  const isMobile = window.innerWidth < 768;

  const revealVariants: Variants = {
    hidden: { 
      opacity: 0, 
      y: isMobile ? 10 : 60,
      scale: isMobile ? 1 : 0.96
    },
    visible: { 
      opacity: 1, 
      y: 0, 
      scale: 1,
      transition: { 
        duration: isMobile ? 0.4 : 0.9, 
        ease: [0.16, 1, 0.3, 1] 
      } 
    }
  };

  return (
    <motion.section
      id={id}
      initial={isMobile ? "visible" : "hidden"}
      animate={noStyle ? "visible" : undefined}
      whileInView={noStyle ? undefined : "visible"}
      viewport={{ once: true, margin: "-50px" }}
      variants={revealVariants}
      className={noStyle ? "" : "relative w-full py-6 sm:py-20 first:pt-0"}
    >
      <div className={noStyle ? "" : "container mx-auto px-4 sm:px-6 relative z-10"}>
        {/* Layered Card: Solid background for maximum Safari performance */}
        <div className={noStyle ? "" : "bg-white dark:bg-slate-900/90 rounded-[2.5rem] sm:rounded-[3rem] border border-slate-200/50 dark:border-white/10 shadow-[0_32px_64px_-12px_rgba(0,0,0,0.08)] dark:shadow-none overflow-hidden transition-[background-color,border-color,shadow] duration-500"}>
          {children}
        </div>
      </div>
      
      {/* Atmosphere Glow - Swapped blur for radial gradient for zero paint cost on Safari */}
      {!isMobile && !noStyle && (
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-[radial-gradient(circle,rgba(59,130,246,0.04)_0%,transparent_70%)] dark:bg-[radial-gradient(circle,rgba(59,130,246,0.08)_0%,transparent_70%)] -z-10 pointer-events-none" />
      )}
    </motion.section>
  );
};

const MainApp = ({ signOut, authUser }: { signOut?: () => void; authUser?: any }) => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [isPostCollege, setIsPostCollege] = useState(false);
  const [isAdvisor, setIsAdvisor] = useState(false);
  const s3Client = useRef<S3Client>(undefined);
  const dynamoDbClient = useRef<DynamoDBClient>(undefined);
  const lambdaClient = useRef<LambdaClient>(undefined);
  const [users, setUsers] = useState<User[]>([]);
  const [user, setUser] = useState<User>();
  const [showAlert, setShowAlert] = useState(false);
  const [alertStatus, setAlertStatus] = useState<'SUCCESS' | 'ERROR' | undefined>(undefined);
  const [alertMessage, setAlertMessage] = useState<string>();
  const [alertSubMessages, setAlertSubMessages] = useState<string[]>([]);
  const alertTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isContactsLoaded, setIsContactsLoaded] = useState(false);

  const fetchUsersAndSetState = async (initialDdbClient?: DynamoDBClient, initialUsername?: string) => {
    const client = initialDdbClient || dynamoDbClient.current;
    const currentUsername = initialUsername || authUser?.username || MOCK_USER.cognitoUserId;
    if (!client || !currentUsername) return;

    try {
      setIsContactsLoaded(false);

      // FAST PATH: Fetch individual user first to unblock UI
      const getUserCommand = new GetItemCommand({
        TableName: 'MitchellFC',
        Key: { cognitoUserId: { S: currentUsername } }
      });
      const userOutput = await client.send(getUserCommand);
      if (userOutput.Item) {
        const currentUser = unmarshall(userOutput.Item) as User;
        setUser(currentUser);
        setTelemetryUser(currentUser);
        const html = document.querySelector('html');
        localStorage.setItem('darkMode', currentUser.darkMode.toString());
        currentUser.darkMode ? html?.classList.add('dark') : html?.classList.remove('dark');
      }

      // BACKGROUND: Scan for all users
      let lastEvaluatedKey: Record<string, AttributeValue> | undefined = undefined;
      const allUsers: User[] = [];
      do {
        const command: ScanCommand = new ScanCommand({ TableName: 'MitchellFC', ExclusiveStartKey: lastEvaluatedKey });
        const output: ScanCommandOutput = await client.send(command);
        if (output.Items) {
          allUsers.push(...output.Items.map((item: Record<string, AttributeValue>) => unmarshall(item) as User));
        }
        lastEvaluatedKey = output.LastEvaluatedKey;
      } while (lastEvaluatedKey);

      if (allUsers.length > 0) {
        setUsers(allUsers);
        // Fallback for user if GetItem failed
        if (!userOutput.Item) {
          const userFromList = allUsers.filter((u) => u.cognitoUserId === currentUsername)[0];
          if (userFromList) {
            setUser(userFromList);
            setTelemetryUser(userFromList);
            const html = document.querySelector('html');
            localStorage.setItem('darkMode', userFromList.darkMode.toString());
            userFromList.darkMode ? html?.classList.add('dark') : html?.classList.remove('dark');
          }
        }
      }
    } catch (error) {
      console.error("Failed to fetch users:", error);
    } finally {
      setIsContactsLoaded(true);
    }
  };

  const onShowAlert = (status: 'SUCCESS' | 'ERROR', message: string, subMessages: string[] = []) => {
    setAlertStatus(status);
    setAlertMessage(message);
    setAlertSubMessages(subMessages);
    if (alertTimeoutRef.current) clearTimeout(alertTimeoutRef.current);
    setShowAlert(true);
  };

  useEffect(() => {
    const initApp = async () => {
      try {
        if (isDemo()) {
          setIsAdmin(true);
          setIsAdvisor(true);
          setIsPostCollege(true);
          const mockDdb = new MockDynamoDBClient() as any as DynamoDBClient;
          dynamoDbClient.current = mockDdb;
          s3Client.current = new MockS3Client() as any as S3Client;
          lambdaClient.current = new MockLambdaClient() as any as LambdaClient;
          
          // Theme Override for Demo Mode
          const urlParams = new URLSearchParams(window.location.search);
          const themeParam = urlParams.get('theme');
          const isDark = themeParam ? themeParam === 'dark' : MOCK_USER.darkMode;
          
          const demoUser = { ...MOCK_USER, darkMode: isDark };
          setUser(demoUser);
          setUsers([demoUser, ...MOCK_USERS.filter(u => u.cognitoUserId !== demoUser.cognitoUserId)]);
          
          const html = document.querySelector('html');
          localStorage.setItem('darkMode', isDark.toString());
          isDark ? html?.classList.add('dark') : html?.classList.remove('dark');
          
          setIsContactsLoaded(true);
          return;
        }

        if (!authUser) return;

        const session = await fetchAuthSession();
        const groups = (session.tokens?.accessToken?.payload?.['cognito:groups'] as string[]) || [];
        
        if (groups.includes('Admins')) setIsAdmin(true);
        if (groups.includes('Advisors')) setIsAdvisor(true);
        if (groups.includes('Post_College')) setIsPostCollege(true);

        const credentialProvider = async () => {
          const { credentials } = await fetchAuthSession();
          if (!credentials) throw new Error('No credentials');
          return credentials;
        };

        if (session.credentials) {
           initializeRum(session.credentials);
        }

        const clientConfig = {
          region: 'us-east-1',
          maxAttempts: 5,
          credentials: credentialProvider,
        };

        const ddbClient = new DynamoDBClient(clientConfig);
        lambdaClient.current = new LambdaClient(clientConfig);
        s3Client.current = new S3Client(clientConfig);
        dynamoDbClient.current = ddbClient;
        fetchUsersAndSetState(ddbClient, authUser?.username);
      } catch (error) {
        console.error('Failed to initialize app:', error);
      }
    };
    initApp();
  }, [authUser]);

  useEffect(() => {
    if (showAlert) {
      alertTimeoutRef.current = setTimeout(() => setShowAlert(false), 5000);
      return () => { if (alertTimeoutRef.current) clearTimeout(alertTimeoutRef.current); };
    }
  }, [showAlert]);

  const toastVariants: Variants = {
    hidden: { opacity: 0, y: 20, scale: 0.95 },
    visible: { 
      opacity: 1, 
      y: 0, 
      scale: 1, 
      transition: { 
        type: 'spring',
        damping: 25,
        stiffness: 350
      } 
    },
    exit: { 
      opacity: 0, 
      scale: 0.95,
      transition: { duration: 0.2, ease: 'easeIn' } 
    },
  };

  return (
    <div className="min-h-screen bg-slate-100 dark:bg-black selection:bg-blue-500/30 transition-colors duration-700">
      <Header 
        dynamoDbClient={dynamoDbClient.current as DynamoDBClient}
        user={user as User} 
        allUsers={users}
        isAdmin={isAdmin}
        signOut={signOut!} 
        onSettingsSaved={fetchUsersAndSetState} 
        onShowAlert={onShowAlert}
      />

      <main className="relative pb-24 sm:pb-32 overflow-x-hidden pt-[52px] sm:pt-[60px] lg:pt-[64px]">
        <AnimatePresence>
          {showAlert && (
            <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[1000] w-full max-w-sm px-4">
              <motion.div
                key="toast-alert"
                variants={toastVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className={`rounded-2xl shadow-xl backdrop-blur-md p-5 pr-12 border
                  ${alertStatus === 'SUCCESS' ? 'bg-green-50/90 border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-300' : 'bg-red-50/90 border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300'}
                `}
              >
                <div className="flex items-center gap-3">
                  <div className={`h-8 w-8 rounded-full flex items-center justify-center ${alertStatus === 'SUCCESS' ? 'bg-green-100 dark:bg-green-800/30' : 'bg-red-100 dark:bg-red-800/30'}`}>
                    {alertStatus === 'SUCCESS' ? '✓' : '!'}
                  </div>
                  <div>
                    <h5 className="font-black text-sm uppercase tracking-widest">{alertStatus === 'SUCCESS' ? 'Success' : 'Attention'}</h5>
                    <p className="text-sm font-medium opacity-90">{alertMessage}</p>
                  </div>
                </div>
                <button onClick={() => setShowAlert(false)} className="absolute top-4 right-4 p-1 rounded-full hover:bg-black/5 transition-colors">
                  <XIcon className="h-5 w-5 opacity-50" aria-hidden="true" />
                </button>
                {alertSubMessages.length > 0 && (
                  <ul className="mt-3 space-y-1 pl-11">
                    {alertSubMessages.map((msg, i) => <li key={i} className="text-xs opacity-70 list-disc font-medium">{msg}</li>)}
                  </ul>
                )}
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        <div id="home">
          <LandingSection />
        </div>

        <SectionWrapper id="feed" noStyle={window.location.pathname === '/feed'}>
          <FeedSection 
            ddbClient={dynamoDbClient.current}
            s3Client={s3Client.current}
            lambdaClient={lambdaClient.current}
            currentUser={user}
            allUsers={users}
            isAdmin={isAdmin}
          />
        </SectionWrapper>

        <Suspense fallback={<div className="flex justify-center p-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div></div>}>
          <SectionWrapper id="documentDisplayer">
            <DocumentsSection 
              lambdaClient={lambdaClient.current as LambdaClient}
              s3Client={s3Client.current as S3Client}
              isAdmin={isAdmin}
              isPostCollege={isPostCollege}
              isAdvisor={isAdvisor}
              onShowAlert={onShowAlert}
            />
          </SectionWrapper>

          <SectionWrapper id="contacts">
            <ContactsSection users={users} isAdmin={isAdmin} isContactsLoaded={isContactsLoaded} />
          </SectionWrapper>

          <SectionWrapper id="photos">
            <PhotosSection />
          </SectionWrapper>

          <SectionWrapper id="videos">
            <VideosSection />
          </SectionWrapper>
        </Suspense>
      </main>
    </div>
  );
};

const components = {
  Header() {
    return (
      <motion.div 
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
        className="text-center pb-8 pt-12 flex flex-col items-center"
      >
        <motion.div 
          initial={{ scale: 0.85, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
          className="mb-10 relative group cursor-default"
        >
          <div className="relative w-40 sm:w-48 p-6 rounded-[3rem] shadow-[0_20px_50px_-12px_rgba(59,130,246,0.5)] dark:shadow-none border border-slate-200/50 dark:border-slate-700/50 overflow-hidden bg-white flex items-center justify-center transform transition-all duration-700 hover:scale-105">
            <img className="w-full h-auto block" src={logo} alt="Mitchell Family Logo" />
          </div>
        </motion.div>
        <h2 className="text-4xl sm:text-5xl font-black tracking-tighter text-slate-900 dark:text-white">
          Mitchell Family Council
        </h2>
        <p className="mt-4 text-base font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest">
          Private Family Portal
        </p>
      </motion.div>
    );
  },
};

const AuthWrapper = () => {
  const { route, user, signOut } = useAuthenticator((context) => [
    context.route,
    context.user,
  ]);

  useEffect(() => {
    const savedDarkMode = localStorage.getItem('darkMode');
    const html = document.querySelector('html');
    if (savedDarkMode === 'true') {
      html?.classList.add('dark');
    } else {
      html?.classList.remove('dark');
    }
  }, []);

  useEffect(() => {
    if (route === 'authenticated' && user) {
      if (!isRumInitialized()) {
         fetchAuthSession().then(session => {
            if (session.credentials) initializeRum(session.credentials);
         });
      }
      recordTelemetry('UserLoggedIn', { 
        username: user.username
      });
    }
  }, [route, user]);

  if (route === 'authenticated' || isDemo()) {
    return <MainApp signOut={signOut} authUser={user} />;
  }


  return (
    <div className="min-h-screen bg-slate-100 dark:bg-black flex items-center justify-center px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-96 h-96 bg-[radial-gradient(circle,rgba(59,130,246,0.12)_0%,transparent_70%)] rounded-full -translate-y-1/2 translate-x-1/2 pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-[radial-gradient(circle,rgba(99,102,241,0.12)_0%,transparent_70%)] rounded-full translate-y-1/2 -translate-x-1/2 pointer-events-none" />
      
      <motion.div 
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
        className="w-full max-w-md relative z-10"
      >
        <style>
          {`
            [data-amplify-authenticator] {
              --amplify-colors-brand-primary-80: #2563eb;
              --amplify-colors-brand-primary-90: #1d4ed8;
              --amplify-colors-brand-primary-100: #1e3a8a;
              --amplify-components-authenticator-router-box-shadow: 0 25px 50px -12px rgb(0 0 0 / 0.15), 0 0 0 1px rgb(0 0 0 / 0.05);
              --amplify-components-authenticator-router-border-width: 0;
              --amplify-components-authenticator-router-border-radius: 2rem;
              --amplify-radii-medium: 1.25rem;
              --amplify-components-fieldcontrol-border-radius: 1rem;
              --amplify-components-button-border-radius: 1rem;
              --amplify-components-fieldcontrol-border-color: #e2e8f0;
              --amplify-components-fieldcontrol-focus-box-shadow: 0 0 0 4px #bfdbfe;
            }
            [data-amplify-authenticator] [data-amplify-router] {
              border-radius: 2rem !important;
              overflow: hidden !important;
            }
            .dark [data-amplify-authenticator] {
              --amplify-colors-background-primary: #1e293b;
              --amplify-colors-background-secondary: #0f172a;
              --amplify-components-authenticator-router-background-color: #1e293b;
              --amplify-components-authenticator-router-box-shadow: 0 25px 50px -12px rgb(0 0 0 / 0.5), 0 0 0 1px rgb(255 255 255 / 0.1);
              --amplify-components-fieldcontrol-background-color: #0f172a;
              --amplify-components-fieldcontrol-border-color: #334155;
              --amplify-components-fieldcontrol-color: #f8fafc;
              --amplify-components-fieldcontrol-focus-box-shadow: 0 0 0 4px #3b82f633;
              --amplify-colors-font-primary: #f8fafc;
              --amplify-colors-font-secondary: #94a3b8;
              --amplify-colors-font-interactive: #e2e8f0;
              --amplify-colors-border-primary: #334155;
              --amplify-colors-border-focus: #3b82f6;
            }
            .dark .amplify-input {
              color: #f8fafc !important;
              background-color: #0f172a !important;
              border-color: #334155 !important;
            }
          .dark .amplify-input:focus {
            border-color: #3b82f6 !important;
            box-shadow: 0 0 0 4px #3b82f633 !important;
            outline: none !important;
          }
          .dark .amplify-label {
            color: #cbd5e1 !important;
          }
          .dark .amplify-field__show-password {
            color: #94a3b8 !important;
            background-color: #0f172a !important;
            border-color: #334155 !important;
          }
          .dark .amplify-field__show-password:hover {
            background-color: #1e293b !important;
            color: #f8fafc !important;
          }
          .dark .amplify-field__show-password:focus {
            border-color: #3b82f6 !important;
            box-shadow: 0 0 0 4px #3b82f633 !important;
            outline: none !important;
          }
          .dark .amplify-button[data-variation="link"] {
            color: #60a5fa !important;
          }
          .dark .amplify-button[data-variation="link"]:hover {
            color: #93c5fd !important;
            background-color: transparent !important;
          }
            .amplify-button--primary {
              background-color: #2563eb !important;
              font-weight: 800 !important;
              text-transform: uppercase !important;
              letter-spacing: 0.1em !important;
              padding: 0.75rem !important;
            }
          `}
        </style>
        <Authenticator hideSignUp loginMechanisms={['email']} components={components} />
      </motion.div>
    </div>
  );
};

export default function SinglePage() {
  return (
    <Authenticator.Provider>
      <AuthWrapper />
    </Authenticator.Provider>
  );
}
